/**
 * O pote de estrelas do painel — o `StarlightJarWidget` de
 * `lib/features/starlight_jar/presentation/screens/starlight_jar_widget.dart`,
 * com o `GlassJar`, o `NoteDisplayDialog` e o `DropStarDialog` dentro.
 *
 * Um vidro com as estrelas de vocês flutuando lá dentro. Toca no pote e ele
 * chacoalha, sobe um bilhete e abre. O botão do dado faz o mesmo com festa, e
 * o da estrela guarda uma nova.
 *
 * As contas — a ordem, o "neste dia", o filtro, o sorteio e o voo de cada
 * estrela dentro do vidro — moram em `pote/estrela`, no pacote comum, porque
 * são as mesmas no celular. Aqui fica o que é do navegador:
 *
 * - O `_JarStarFieldPainter` vira um `<canvas>` com `requestAnimationFrame`. É
 *   a mesma escolha da `Planta`: a regra de não escrever animação à mão vale
 *   para o que o navegador anima sozinho, e não para um desenho que precisa ser
 *   repintado a cada quadro. Quem pediu menos movimento recebe o pote parado no
 *   instante zero, com as estrelas onde elas nascem.
 * - O `MaskFilter.blur` de cada estrela vira o `filter` do contexto, ligado uma
 *   vez antes do laço em vez de uma por estrela.
 * - O `_motionCache` do Flutter guarda por `id.hashCode`; aqui guarda por `id`
 *   mesmo, que é o que existe. Sem esse cache o `Acaso` seria refeito quarenta
 *   vezes por quadro, e ele trabalha em `BigInt`.
 * - As cinco cores das estrelas: as três primeiras são do tema e giram com ele,
 *   como o getter de lá; as duas últimas estão escritas à mão no Flutter e por
 *   isso ficam paradas aqui também.
 * - O `ClipPath` do vidro vira um `clip-path: inset(... round ...)`, que corta o
 *   `<canvas>` e a estrela que voa de uma vez só — lá as três camadas estão
 *   dentro do mesmo `ClipPath`.
 * - As duas `Curves` que o CSS não tem (`bounceOut` e `easeOutExpo`) estão
 *   escritas em JavaScript logo abaixo, porque quem move a estrela é o mesmo
 *   laço de quadros que já está correndo.
 * - O `SnackBar` do pote vazio vira uma linha em `aria-live` embaixo do pote: a
 *   casa não tem tarja flutuante, e inventar uma para um recado de dois
 *   segundos seria construir um andar inteiro para uma frase.
 * - O emoji de cada categoria e o do título viram desenhos da casa, que é a
 *   regra desta parte do app e o que as duas telas de Lembranças já fazem.
 */

import { useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  CATEGORIAS_DA_ESTRELA,
  COLECOES,
  POTE,
  TETO_DO_POTE,
  categoriaDaEstrela,
  dataDoBilhete,
  estrelasDesteDia,
  filtrarEstrelas,
  gravarData,
  lerData,
  mesDia,
  movimentoDaEstrela,
  novoId,
  ordenarEstrelas,
  pontosDaEstrela,
  pousoDaEstrela,
  sortearEstrela,
  traduzir,
  usarColecao,
  usarSessao,
  type DocEstrela,
  type Estrela,
  type MovimentoDaEstrela,
} from '@nosso-sempre/shared';
import { armazem } from '../nucleo/api';
import { girar, paletaAtiva } from '../nucleo/paleta';
import { Folha } from './Folha';
import { Icone } from './Icone';
import { Secao } from './Secao';
import './previaDoPote.css';

/** O `_idleController`: uma volta inteira a cada catorze segundos. */
const VOLTA = 14000;

/** O `_shakeController`. */
const CHACOALHO = 600;

/** O `_dropController`, com a `Curves.bounceOut`. */
const QUEDA = 1000;

/** O `_floatController`, com a `Curves.easeOutExpo`. */
const SUBIDA = 500;

/** O `SnackBar(duration: Duration(seconds: 2))` do pote vazio. */
const RECADO = 2000;

/** O lado da caixa em que a estrela que voa é desenhada. */
const VOADORA = 40;

/** O `_tags.length < 5` do formulário. */
const MARCAS = 5;

/** As quinze faíscas da festa, as mesmas do `CartaoDeSorteio`. */
const FAISCAS = 15;

/**
 * O `_StarMotion._colors`. As três primeiras passam pelo `_t` do tema e giram
 * com ele; `Color(0xFFFFF176)` e `Color(0xFF80DEEA)` estão escritas à mão lá e
 * ficam paradas aqui, como lá.
 */
const BASES = ['#f4c2c2', '#e8d5b7', '#d4b5d6'] as const;
const FIXAS = ['#fff176', '#80deea'] as const;

let paletaDasCores = '';
let coresGuardadas: string[] = [];

/** As cinco cores, refeitas só quando a casa troca de tema. */
function cores(): string[] {
  const p = paletaAtiva();
  if (p.id !== paletaDasCores) {
    paletaDasCores = p.id;
    coresGuardadas = [...BASES.map((c) => girar(c, p)), ...FIXAS];
  }
  return coresGuardadas;
}

/** A `Curves.easeOutExpo`. */
const expo = (t: number): number => (t >= 1 ? 1 : 1 - Math.pow(2, -10 * t));

/** A `Curves.bounceOut`, quique por quique. */
function quique(t: number): number {
  if (t < 1 / 2.75) return 7.5625 * t * t;
  if (t < 2 / 2.75) {
    const u = t - 1.5 / 2.75;
    return 7.5625 * u * u + 0.75;
  }
  if (t < 2.5 / 2.75) {
    const u = t - 2.25 / 2.75;
    return 7.5625 * u * u + 0.9375;
  }
  const u = t - 2.625 / 2.75;
  return 7.5625 * u * u + 0.984375;
}

const menosMovimento = (): boolean => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const espera = (ms: number): Promise<void> => new Promise((pronto) => setTimeout(pronto, ms));

/** Uma estrela em trânsito: a que cai ao ser guardada e a que sobe para abrir. */
interface Voo {
  tipo: 'queda' | 'subida';
  /** O instante do `performance.now()` em que começou. */
  inicio: number;
  duracao: number;
  /** O `reverse()` do controlador: a subida desfeita quando o bilhete fecha. */
  volta: boolean;
  de: { x: number; y: number };
  para: { x: number; y: number };
  cor: string;
}

export function PreviaDoPote() {
  const { quem } = usarSessao();
  const estrelas = usarColecao<DocEstrela>(COLECOES.potePrateado);

  const [busca, setBusca] = useState('');
  const [categoria, setCategoria] = useState<string | null>(null);
  const [aberta, setAberta] = useState<{ estrela: Estrela; festa: boolean } | null>(null);
  const [escrevendo, setEscrevendo] = useState(false);
  const [chacoalha, setChacoalha] = useState(false);
  const [vazio, setVazio] = useState(false);

  const tela = useRef<HTMLCanvasElement>(null);
  const voadora = useRef<HTMLSpanElement>(null);
  const voo = useRef<Voo | null>(null);
  const memoria = useRef(new Map<string, MovimentoDaEstrela>());
  const noVidro = useRef<ReadonlyArray<Estrela>>([]);
  const ocupado = useRef(false);
  const vivo = useRef(true);

  /** O `getStarNotes`: as quarenta mais novas. A conta do selo sai daqui. */
  const todas = useMemo(() => ordenarEstrelas(estrelas).slice(0, TETO_DO_POTE), [estrelas]);

  /** O `_filterNotes(_notes)`: só o que o pintor desenha. */
  const filtradas = useMemo(
    () => filtrarEstrelas(todas, busca, categoria),
    [todas, busca, categoria],
  );

  /** O `_onThisDayNotes`, que abre a faixa dourada em cima. */
  const nesteDia = useMemo(() => estrelasDesteDia(estrelas), [estrelas]);

  useEffect(() => {
    noVidro.current = filtradas;
  }, [filtradas]);

  useEffect(() => {
    vivo.current = true;
    return () => {
      vivo.current = false;
    };
  }, []);

  // ── O laço de quadros ──────────────────────────────────────────────

  useEffect(() => {
    const quadro = tela.current;
    const ctx = quadro?.getContext('2d');
    if (!quadro || !ctx) return;

    const escala = window.devicePixelRatio || 1;
    quadro.width = Math.round(POTE.largura * escala);
    quadro.height = Math.round(POTE.altura * escala);
    ctx.setTransform(escala, 0, 0, escala, 0, 0);

    function pintar(t: number) {
      if (!ctx) return;
      ctx.clearRect(0, 0, POTE.largura, POTE.altura);
      // O `filter` embaça o traço inteiro, que é o que o `MaskFilter.blur` de
      // lá faz. Ligado uma vez: ele vale para todos os preenchimentos abaixo.
      ctx.filter = 'blur(4px)';
      const tinta = cores();

      for (const x of noVidro.current) {
        let m = memoria.current.get(x.id);
        if (!m) {
          m = movimentoDaEstrela(x.id);
          memoria.current.set(x.id, m);
        }
        const p = pousoDaEstrela(m, t);
        const pontos = pontosDaEstrela(24 * p.escala);

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.giro);
        ctx.globalAlpha = p.opacidade;
        ctx.fillStyle = tinta[m.cor] ?? tinta[0];
        ctx.beginPath();
        ctx.moveTo(pontos[0].x, pontos[0].y);
        for (let i = 1; i < pontos.length; i++) ctx.lineTo(pontos[i].x, pontos[i].y);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
      }
      ctx.filter = 'none';
    }

    function voar(agora: number) {
      const no = voadora.current;
      if (!no) return;
      const v = voo.current;
      if (!v) {
        no.style.visibility = 'hidden';
        return;
      }

      const bruto = Math.min(1, (agora - v.inicio) / v.duracao);
      const andar = v.volta ? 1 - bruto : bruto;
      const curva = v.tipo === 'queda' ? quique(andar) : expo(andar);
      const x = v.de.x + (v.para.x - v.de.x) * curva;
      const y = v.de.y + (v.para.y - v.de.y) * curva;
      // O `StarWidget` cresce de 24 a 40 na subida; a queda tem tamanho fixo.
      const lado = v.tipo === 'queda' ? 24 : 24 + 16 * curva;
      // O giro sai do controlador cru, e não da curva, como nos dois lá.
      const roda = v.tipo === 'queda' ? andar * Math.PI * 2 : andar * Math.PI;

      no.style.visibility = 'visible';
      no.style.color = v.cor;
      // Lá o `Positioned` põe o canto da caixa no ponto e o desenho cresce a
      // partir dele; aqui o `scale` cresce a partir do meio, então o canto é
      // recolocado à mão. Sem isto a estrela derivaria oito pixels ao crescer.
      no.style.transform =
        `translate(${x + lado / 2 - VOADORA / 2}px, ${y + lado / 2 - VOADORA / 2}px)` +
        ` rotate(${roda}rad) scale(${lado / VOADORA})`;

      if (bruto >= 1 && (v.volta || v.tipo === 'queda')) {
        voo.current = null;
        no.style.visibility = 'hidden';
      }
    }

    if (menosMovimento()) {
      pintar(0);
      return;
    }

    let pedido = 0;
    const zero = performance.now();
    const laco = (agora: number) => {
      pintar(((agora - zero) % VOLTA) / VOLTA);
      voar(agora);
      pedido = requestAnimationFrame(laco);
    };
    pedido = requestAnimationFrame(laco);
    return () => cancelAnimationFrame(pedido);
  }, []);

  // ── O que o dedo faz ───────────────────────────────────────────────

  function avisarVazio() {
    setVazio(true);
    setTimeout(() => {
      if (vivo.current) setVazio(false);
    }, RECADO);
  }

  /** O `_playFloatAndReveal`: sobe a estrela e só então abre o bilhete. */
  async function subir(estrela: Estrela, festa: boolean) {
    if (!menosMovimento()) {
      voo.current = {
        tipo: 'subida',
        inicio: performance.now(),
        duracao: SUBIDA,
        volta: false,
        de: { x: 200, y: 370 },
        para: { x: 200, y: 100 },
        cor: girar('#c2185b', paletaAtiva()),
      };
      await espera(SUBIDA);
      if (!vivo.current) return;
    }
    setAberta({ estrela, festa });
  }

  /** O `_onJarTap`. */
  async function tocarNoPote() {
    if (ocupado.current) return;
    ocupado.current = true;
    try {
      if (!menosMovimento()) {
        setChacoalha(true);
        await espera(CHACOALHO);
        if (!vivo.current) return;
        setChacoalha(false);
      }
      const escolhida = sortearEstrela(estrelas);
      if (!escolhida) {
        avisarVazio();
        return;
      }
      await subir(escolhida, false);
    } finally {
      ocupado.current = false;
    }
  }

  /** O `_surpriseReveal`: sorteia primeiro, e só então solta a festa. */
  async function surpresa() {
    if (ocupado.current) return;
    const escolhida = sortearEstrela(estrelas);
    if (!escolhida) {
      avisarVazio();
      return;
    }
    ocupado.current = true;
    try {
      await subir(escolhida, true);
    } finally {
      ocupado.current = false;
    }
  }

  /** O `_showNoteDialog` quando fecha: desfaz a subida e some com a estrela. */
  function fecharBilhete() {
    setAberta(null);
    const v = voo.current;
    if (v && v.tipo === 'subida' && !v.volta) {
      voo.current = { ...v, inicio: performance.now(), volta: true };
    }
  }

  /** O `_startDropAnimation` mais o `addStar`. */
  async function guardar(conteudo: string, cat: string, marcas: string[]) {
    const texto = conteudo.trim();
    if (!texto) return;
    setEscrevendo(false);

    if (!menosMovimento()) {
      voo.current = {
        tipo: 'queda',
        inicio: performance.now(),
        duracao: QUEDA,
        volta: false,
        de: { x: POTE.largura / 2, y: -50 },
        para: { x: 120 + Math.random() * 160, y: 300 + Math.random() * 60 },
        cor: girar('#e8d5b7', paletaAtiva()),
      };
    }

    const agora = new Date();
    await armazem.gravar(COLECOES.potePrateado, novoId(), {
      content: texto,
      // Minúsculo como o `addStar` grava: as telas comparam autor por aqui.
      author: (quem ?? '').toLowerCase(),
      timestamp: gravarData(agora),
      category: cat,
      tags: marcas,
      monthDay: mesDia(agora),
    });
  }

  const quantas = todas.length;

  return (
    <Secao
      icone="brilho"
      cor="var(--ouro)"
      titulo={traduzir('Pote de estrelas')}
      legenda={traduzir('gratidão sob as estrelas')}
      ponta={
        <Link
          className="secao-seta"
          to="/lembrancas"
          aria-label={traduzir('Ver todas as lembranças')}
        >
          <Icone nome="avancar" tamanho={18} />
        </Link>
      }
    >
      {nesteDia.length > 0 ? (
        <button
          className="pote-hoje"
          type="button"
          onClick={() => setAberta({ estrela: nesteDia[0], festa: false })}
          aria-label={traduzir('Memória deste dia. Toque para ver.')}
        >
          <Icone nome="calendario" tamanho={18} />
          <span className="pote-hoje-dizeres">
            <span className="pote-hoje-titulo">{traduzir('Neste dia')}</span>
            <span className="pote-hoje-texto">{nesteDia[0].dados.content}</span>
          </span>
          <Icone nome="avancar" tamanho={20} />
        </button>
      ) : null}

      <div className="pote-busca">
        <Icone nome="busca" tamanho={18} />
        <label className="so-leitor" htmlFor="pote-procurar">
          {traduzir('Procurar nas estrelas')}
        </label>
        <input
          id="pote-procurar"
          type="text"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder={traduzir('Search stars…')}
        />
        {busca ? (
          <button type="button" className="pote-limpar" onClick={() => setBusca('')}>
            <Icone nome="fechar" tamanho={16} rotulo={traduzir('Limpar a busca')} />
          </button>
        ) : null}
      </div>

      <div className="pote-marcas" role="group" aria-label={traduzir('Filtrar por categoria')}>
        <button
          type="button"
          className={`pote-marca${categoria === null ? ' aqui' : ''}`}
          aria-pressed={categoria === null}
          onClick={() => setCategoria(null)}
        >
          {traduzir('Tudo')}
        </button>
        {CATEGORIAS_DA_ESTRELA.map((c) => (
          <button
            key={c.chave}
            type="button"
            className={`pote-marca${categoria === c.chave ? ' aqui' : ''}`}
            aria-pressed={categoria === c.chave}
            onClick={() => setCategoria(categoria === c.chave ? null : c.chave)}
          >
            {traduzir(c.nome)}
          </button>
        ))}
      </div>

      <div className="pote-caixa">
        <div className="pote-palco">
          <button
            className={`pote-vidro${chacoalha ? ' chacoalha' : ''}`}
            type="button"
            onClick={() => void tocarNoPote()}
            aria-label={traduzir(
              'Pote de vidro com os bilhetes de gratidão. Toque para sortear um.',
            )}
          >
            <span className="pote-corpo" aria-hidden="true" />
            <span className="pote-luz" aria-hidden="true" />
            <span className="pote-tampa" aria-hidden="true" />
          </button>

          {quantas > 0 ? (
            <span className="pote-conta" aria-hidden="true">
              {quantas} {quantas === 1 ? 'star' : 'stars'}
            </span>
          ) : null}

          <span className="pote-vidraca" aria-hidden="true">
            <canvas ref={tela} className="pote-campo" width={POTE.largura} height={POTE.altura} />
            <span ref={voadora} className="pote-voadora">
              <Icone nome="estrela" tamanho={VOADORA} preenchido />
            </span>
          </span>

          <div className="pote-acoes">
            <button
              className="pote-acao surpresa"
              type="button"
              onClick={() => void surpresa()}
              title={traduzir('Sortear com festa')}
            >
              <Icone nome="dado" tamanho={20} rotulo={traduzir('Sortear uma estrela com festa')} />
            </button>
            <button
              className="pote-acao"
              type="button"
              onClick={() => setEscrevendo(true)}
              title={traduzir('Guardar uma estrela')}
            >
              <Icone
                nome="estrela"
                tamanho={22}
                preenchido
                rotulo={traduzir('Guardar uma estrela')}
              />
            </button>
          </div>
        </div>
      </div>

      <p className="pote-recado" role="status">
        {vazio ? traduzir('No stars in the jar yet — drop one first!') : ''}
      </p>

      <Bilhete aberta={aberta} aoFechar={fecharBilhete} />

      <Folha
        aberta={escrevendo}
        aoFechar={() => setEscrevendo(false)}
        titulo={traduzir('Drop a Star')}
        centro
        classe="pote-folha"
      >
        <Formulario
          aoGuardar={(t, c, m) => void guardar(t, c, m)}
          aoDesistir={() => setEscrevendo(false)}
        />
      </Folha>
    </Secao>
  );
}

// ── O bilhete aberto ─────────────────────────────────────────────────

/**
 * O `NoteDisplayDialog`.
 *
 * Não é uma `Folha` da casa pelo mesmo motivo da festa do sorteio: as faíscas
 * saem do cartão e vão a duzentos pixels do centro, e a folha as cortaria na
 * borda.
 *
 * ponytail: as faíscas são as mesmas do `CartaoDeSorteio`, escritas duas vezes
 * porque cada uma nasce dentro do diálogo a que pertence. Quando uma terceira
 * tela pedir a mesma roda, ela vira peça da casa.
 */
function Bilhete({
  aberta,
  aoFechar,
}: {
  aberta: { estrela: Estrela; festa: boolean } | null;
  aoFechar: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const no = ref.current;
    if (!no) return;
    if (aberta && !no.open) no.showModal();
    else if (!aberta && no.open) no.close();
  }, [aberta]);

  function noFundo(e: MouseEvent<HTMLDialogElement>) {
    if (e.target === ref.current) aoFechar();
  }

  const dados = aberta?.estrela.dados;
  const quando = lerData(dados?.timestamp);
  const marcas = dados?.tags ?? [];

  return (
    <dialog
      ref={ref}
      className="bilhete"
      aria-label={dados?.content ?? ''}
      onClick={noFundo}
      onCancel={(e) => {
        e.preventDefault();
        aoFechar();
      }}
    >
      {aberta ? (
        <div className="bilhete-corpo">
          {aberta.festa ? (
            <span className="bilhete-faiscas" aria-hidden="true">
              {Array.from({ length: FAISCAS }, (_, i) => {
                const angulo = (i / FAISCAS) * 2 * Math.PI;
                const distancia = 150 + fixo(i, 0) * 60;
                const tamanho = Math.round(10 + fixo(i, 1) * 20);
                const tom = i % 3 === 0 ? 'rosa' : i % 3 === 1 ? 'ouro' : 'lavanda';
                return (
                  <i
                    key={i}
                    className={i % 2 === 0 ? tom : `${tom} bola`}
                    style={{
                      ['--fx' as string]: `${(Math.cos(angulo) * distancia).toFixed(1)}px`,
                      ['--fy' as string]: `${(Math.sin(angulo) * distancia).toFixed(1)}px`,
                      ['--ftam' as string]: `${tamanho}px`,
                      ['--fatraso' as string]: `${i * 40}ms`,
                    }}
                  >
                    {i % 2 === 0 ? <Icone nome="estrela" tamanho={tamanho} preenchido /> : null}
                  </i>
                );
              })}
            </span>
          ) : null}

          <div className="bilhete-cartao">
            <span className="bilhete-selo">
              {traduzir(categoriaDaEstrela(dados?.category).nome)}
            </span>
            <span className="bilhete-brilho" aria-hidden="true">
              <Icone nome="brilho" tamanho={36} />
            </span>
            <p className="bilhete-texto">{dados?.content}</p>

            {marcas.length > 0 ? (
              <ul className="bilhete-marcas">
                {marcas.map((t) => (
                  <li key={t}>#{t}</li>
                ))}
              </ul>
            ) : null}

            <p className="bilhete-autor">— {(dados?.author ?? '').toUpperCase()}</p>
            {quando ? <p className="bilhete-data">{dataDoBilhete(quando)}</p> : null}

            <button className="bilhete-ok" type="button" onClick={aoFechar}>
              {traduzir('Close')}
            </button>
          </div>
        </div>
      ) : null}
    </dialog>
  );
}

// ── O formulário ─────────────────────────────────────────────────────

/** O `DropStarDialog`, com o miolo dentro da folha da casa. */
function Formulario({
  aoGuardar,
  aoDesistir,
}: {
  aoGuardar: (texto: string, categoria: string, marcas: string[]) => void;
  aoDesistir: () => void;
}) {
  const [texto, setTexto] = useState('');
  const [categoria, setCategoria] = useState('gratitude');
  const [marca, setMarca] = useState('');
  const [marcas, setMarcas] = useState<string[]>([]);

  function somar() {
    const nova = marca.trim().toLowerCase();
    if (!nova || marcas.includes(nova) || marcas.length >= MARCAS) return;
    setMarcas([...marcas, nova]);
    setMarca('');
  }

  return (
    <form
      className="pote-form"
      onSubmit={(e) => {
        e.preventDefault();
        aoGuardar(texto, categoria, marcas);
      }}
    >
      <span className="pote-rotulo">{traduzir('Category')}</span>
      <div className="pote-marcas" role="group" aria-label={traduzir('Category')}>
        {CATEGORIAS_DA_ESTRELA.map((c) => (
          <button
            key={c.chave}
            type="button"
            className={`pote-marca${categoria === c.chave ? ' aqui' : ''}`}
            aria-pressed={categoria === c.chave}
            onClick={() => setCategoria(c.chave)}
          >
            <Icone nome={c.icone} tamanho={14} />
            {traduzir(c.nome)}
          </button>
        ))}
      </div>

      <label className="so-leitor" htmlFor="pote-texto">
        {traduzir('What are you grateful for today?')}
      </label>
      <textarea
        id="pote-texto"
        rows={3}
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        placeholder={traduzir('What are you grateful for today?')}
      />

      <div className="pote-linha-marca">
        <label className="so-leitor" htmlFor="pote-etiqueta">
          {traduzir('Add a tag…')}
        </label>
        <input
          id="pote-etiqueta"
          type="text"
          value={marca}
          onChange={(e) => setMarca(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            // O `onSubmitted` de lá guarda a etiqueta; aqui o Enter dentro de
            // um formulário enviaria a estrela inteira sem querer.
            e.preventDefault();
            somar();
          }}
          placeholder={traduzir('Add a tag…')}
        />
        <button
          type="button"
          className="pote-somar"
          onClick={somar}
          disabled={!marca.trim() || marcas.length >= MARCAS}
        >
          <Icone nome="mais" tamanho={16} rotulo={traduzir('Guardar a etiqueta')} />
        </button>
      </div>

      {marcas.length > 0 ? (
        <ul className="pote-etiquetas">
          {marcas.map((t) => (
            <li key={t}>
              {t}
              <button
                type="button"
                onClick={() => setMarcas(marcas.filter((o) => o !== t))}
                aria-label={traduzir('Tirar a etiqueta {t}', { t })}
              >
                <Icone nome="fechar" tamanho={12} />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="pote-botoes">
        <button className="botao calmo" type="button" onClick={aoDesistir}>
          {traduzir('Cancel')}
        </button>
        <button className="botao" type="submit" disabled={!texto.trim()}>
          {traduzir('Drop it!')}
        </button>
      </div>
    </form>
  );
}

/**
 * O `Random(index)` das faíscas: o índice entra, um número entre zero e um sai,
 * e sai igual sempre. A mesma conta do `CartaoDeSorteio`, pelo mesmo motivo — a
 * sequência do Dart não se reproduz aqui e ninguém precisa dela do outro lado.
 */
function fixo(semente: number, volta: number): number {
  const x = Math.sin(semente * 127.1 + volta * 311.7) * 43758.5453;
  return x - Math.floor(x);
}
